import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Cloud,
  Database,
  Upload,
  Download,
  Server,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Key,
  Globe,
  HardDrive,
  Loader,
  Lock,
  Eye,
  EyeOff,
  Activity,
  Archive,
  Cpu,
  Network,
} from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';
import DraggableTabBar from '../components/DraggableTabBar';
import '../styles/AWSCloudPage.css';
import PrintButton from '../components/PrintButton';

function AWSCloudPage() {
  // Tab management
  const [activeTab, setActiveTab] = useState('configuration');
  const tabsRef = useRef(null);
  const { onKeyDown: handleTabKeyDown } = useArrowNav(tabsRef, 'button', { vertical: false, activateOnFocus: true });

  // AWS Configuration state
  const [awsConfig, setAwsConfig] = useState({
    accessKeyId: '',
    secretAccessKey: '',
    region: 'us-east-1',
    bucketName: '',
  });
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [accountInfo, setAccountInfo] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Infrastructure state
  const [infraFullStatus, setInfraFullStatus] = useState(null);
  const [vpcConfig, setVpcConfig] = useState({});
  const [vpcInfo, setVpcInfo] = useState(null);
  const [s3Config, setS3Config] = useState({ bucketName: '' });
  const [sgVpcId, setSgVpcId] = useState('');

  // Compute state
  const [taskConfig, setTaskConfig] = useState({
    containerImage: 'zignagenix/medical-intelligence:latest',
    cpu: '256',
    memory: '512',
    rdsEndpoint: '',
    secretsArn: '',
  });
  const [serviceConfig, setServiceConfig] = useState({
    desiredCount: '1',
    privateSubnets: '',
    ecsSgId: '',
    targetGroupArn: '',
  });
  const [ecsStatus, setEcsStatus] = useState(null);

  // Load Balancer state
  const [albConfig, setAlbConfig] = useState({
    publicSubnets: '',
    albSgId: '',
    vpcId: '',
    certificateArn: '',
  });
  const [albStatus, setAlbStatus] = useState(null);

  // RDS state
  const [rdsConfig, setRdsConfig] = useState({
    instanceClass: 'db.t3.medium',
    storageGb: '100',
    masterUsername: 'admin',
    masterPassword: '',
    instanceId: 'zignagenix-rds',
  });
  const [showRdsPassword, setShowRdsPassword] = useState(false);
  const [rdsStatus, setRdsStatus] = useState('none');
  const [rdsInstanceInfo, setRdsInstanceInfo] = useState(null);
  const [secretConfig, setSecretConfig] = useState({
    dbUser: 'zignagenix',
    dbPassword: '',
  });
  const [showSecretPassword, setShowSecretPassword] = useState(false);

  // Monitoring state
  const [cloudwatchStatus, setCloudwatchStatus] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [selectedLogGroup, setSelectedLogGroup] = useState('');
  const [logTimeRange, setLogTimeRange] = useState(60);

  // Backup state
  const [backups, setBackups] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [backupInProgress, setBackupInProgress] = useState(false);

  // HIPAA Compliance state
  const [hipaaChecklist, setHipaaChecklist] = useState([
    { id: 1, label: 'VPC with private subnets', checked: false },
    { id: 2, label: 'Encryption at rest (RDS + S3)', checked: false },
    { id: 3, label: 'Encryption in transit (HTTPS/TLS)', checked: false },
    { id: 4, label: 'Multi-AZ RDS deployment', checked: false },
    { id: 5, label: 'Automated backups (35-day retention)', checked: false },
    { id: 6, label: 'CloudWatch monitoring + alarms', checked: false },
    { id: 7, label: 'Audit logging enabled', checked: false },
    { id: 8, label: 'Security groups (least privilege)', checked: false },
    { id: 9, label: 'IAM roles (no root credentials)', checked: false },
    { id: 10, label: 'Secrets Manager for credentials', checked: false },
  ]);

  // Loading states
  const [loading, setLoading] = useState({
    testConnection: false,
    saveConfig: false,
    fullStatus: false,
    createVpc: false,
    describeVpc: false,
    createSg: false,
    createS3: false,
    createIam: false,
    createEcsCluster: false,
    registerTask: false,
    createEcsService: false,
    describeEcs: false,
    createAlb: false,
    describeAlb: false,
    exportDb: false,
    createRds: false,
    checkRdsStatus: false,
    createSecret: false,
    importToRds: false,
    setupCloudwatch: false,
    getCloudwatchStatus: false,
    getRecentLogs: false,
    backup: false,
    listBackups: false,
  });

  // Load saved config on mount
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await window.zignagenixAPI.awsGetConfig();
        if (config) {
          setAwsConfig({
            accessKeyId: config.accessKeyId || '',
            secretAccessKey: config.secretAccessKey || '••••••••',
            region: config.region || 'us-east-1',
            bucketName: config.bucketName || '',
          });
          setConnectionStatus('connected');
        }
      } catch (err) {
        console.error('Failed to load AWS config:', err);
      }
    }
    loadConfig();
  }, []);

  // Test connection
  const handleTestConnection = useCallback(async () => {
    if (!awsConfig.accessKeyId || !awsConfig.secretAccessKey || awsConfig.secretAccessKey.includes('•')) {
      setConnectionError('Please enter AWS credentials');
      return;
    }
    if (!awsConfig.bucketName) {
      setConnectionError('Please enter S3 bucket name');
      return;
    }

    setLoading(prev => ({ ...prev, testConnection: true }));
    setConnectionStatus('testing');
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      const result = await window.zignagenixAPI.awsTestConnection(awsConfig);
      setAccountInfo(result);
      setConnectionStatus('connected');
      setConnectionError(null);
      setSuccessMessage('Connected to AWS successfully');
    } catch (err) {
      setConnectionStatus('error');
      setConnectionError(err.message || 'Connection test failed');
    } finally {
      setLoading(prev => ({ ...prev, testConnection: false }));
    }
  }, [awsConfig]);

  // Save configuration
  const handleSaveConfig = useCallback(async () => {
    if (!awsConfig.accessKeyId || !awsConfig.secretAccessKey || awsConfig.secretAccessKey.includes('•')) {
      setConnectionError('Please enter AWS credentials');
      return;
    }
    if (!awsConfig.bucketName) {
      setConnectionError('Please enter S3 bucket name');
      return;
    }

    setLoading(prev => ({ ...prev, saveConfig: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsSaveConfig(awsConfig);
      setSuccessMessage('AWS configuration saved successfully');
    } catch (err) {
      setConnectionError('Failed to save configuration: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, saveConfig: false }));
    }
  }, [awsConfig]);

  // Full infrastructure status
  const handleFullStatus = useCallback(async () => {
    setLoading(prev => ({ ...prev, fullStatus: true }));
    setConnectionError(null);

    try {
      const status = await window.zignagenixAPI.awsFullStatus();
      setInfraFullStatus(status);
      updateHipaaChecklistFromStatus(status);
    } catch (err) {
      setConnectionError('Failed to get full status: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, fullStatus: false }));
    }
  }, []);

  // Update HIPAA checklist based on status
  const updateHipaaChecklistFromStatus = (status) => {
    if (!status) return;
    setHipaaChecklist(prev => prev.map(item => {
      const checked = status.hipaaCompliance?.[item.id - 1]?.completed || false;
      return { ...item, checked };
    }));
  };

  // Infrastructure - VPC
  const handleCreateVpc = useCallback(async () => {
    setLoading(prev => ({ ...prev, createVpc: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      const result = await window.zignagenixAPI.awsCreateVpc({});
      setVpcInfo(result);
      setSuccessMessage('VPC created successfully');
    } catch (err) {
      setConnectionError('Failed to create VPC: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createVpc: false }));
    }
  }, []);

  const handleDescribeVpc = useCallback(async () => {
    setLoading(prev => ({ ...prev, describeVpc: true }));
    setConnectionError(null);

    try {
      const result = await window.zignagenixAPI.awsDescribeVpc();
      setVpcInfo(result);
    } catch (err) {
      setConnectionError('Failed to describe VPC: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, describeVpc: false }));
    }
  }, []);

  const handleCreateSg = useCallback(async () => {
    if (!sgVpcId) {
      setConnectionError('Please enter VPC ID');
      return;
    }

    setLoading(prev => ({ ...prev, createSg: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsCreateSecurityGroups(sgVpcId);
      setSuccessMessage('Security groups created successfully');
      setSgVpcId('');
    } catch (err) {
      setConnectionError('Failed to create security groups: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createSg: false }));
    }
  }, [sgVpcId]);

  const handleCreateS3 = useCallback(async () => {
    if (!s3Config.bucketName) {
      setConnectionError('Please enter bucket name');
      return;
    }

    setLoading(prev => ({ ...prev, createS3: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsCreateS3Bucket(s3Config.bucketName);
      setSuccessMessage('S3 bucket created successfully');
      setS3Config({ bucketName: '' });
    } catch (err) {
      setConnectionError('Failed to create S3 bucket: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createS3: false }));
    }
  }, [s3Config.bucketName]);

  // Compute - IAM
  const handleCreateIam = useCallback(async () => {
    setLoading(prev => ({ ...prev, createIam: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsCreateIamRoles();
      setSuccessMessage('IAM roles created successfully');
    } catch (err) {
      setConnectionError('Failed to create IAM roles: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createIam: false }));
    }
  }, []);

  // Compute - ECS
  const handleCreateEcsCluster = useCallback(async () => {
    setLoading(prev => ({ ...prev, createEcsCluster: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsCreateEcsCluster({});
      setSuccessMessage('ECS cluster created successfully');
    } catch (err) {
      setConnectionError('Failed to create ECS cluster: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createEcsCluster: false }));
    }
  }, []);

  const handleRegisterTask = useCallback(async () => {
    if (!taskConfig.containerImage || !taskConfig.rdsEndpoint || !taskConfig.secretsArn) {
      setConnectionError('Please fill in all task configuration fields');
      return;
    }

    setLoading(prev => ({ ...prev, registerTask: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsRegisterTask(taskConfig);
      setSuccessMessage('Task definition registered successfully');
    } catch (err) {
      setConnectionError('Failed to register task: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, registerTask: false }));
    }
  }, [taskConfig]);

  const handleCreateEcsService = useCallback(async () => {
    if (!serviceConfig.desiredCount || !serviceConfig.privateSubnets || !serviceConfig.ecsSgId || !serviceConfig.targetGroupArn) {
      setConnectionError('Please fill in all service configuration fields');
      return;
    }

    setLoading(prev => ({ ...prev, createEcsService: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsCreateEcsService(serviceConfig);
      setSuccessMessage('ECS service created successfully');
    } catch (err) {
      setConnectionError('Failed to create ECS service: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createEcsService: false }));
    }
  }, [serviceConfig]);

  const handleDescribeEcs = useCallback(async () => {
    setLoading(prev => ({ ...prev, describeEcs: true }));
    setConnectionError(null);

    try {
      const status = await window.zignagenixAPI.awsDescribeEcs();
      setEcsStatus(status);
    } catch (err) {
      setConnectionError('Failed to describe ECS: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, describeEcs: false }));
    }
  }, []);

  // Load Balancer
  const handleCreateAlb = useCallback(async () => {
    if (!albConfig.publicSubnets || !albConfig.albSgId || !albConfig.vpcId) {
      setConnectionError('Please fill in required ALB fields');
      return;
    }

    setLoading(prev => ({ ...prev, createAlb: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsCreateAlb(albConfig);
      setSuccessMessage('Application Load Balancer created successfully');
    } catch (err) {
      setConnectionError('Failed to create ALB: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createAlb: false }));
    }
  }, [albConfig]);

  const handleDescribeAlb = useCallback(async () => {
    setLoading(prev => ({ ...prev, describeAlb: true }));
    setConnectionError(null);

    try {
      const status = await window.zignagenixAPI.awsDescribeAlb();
      setAlbStatus(status);
    } catch (err) {
      setConnectionError('Failed to describe ALB: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, describeAlb: false }));
    }
  }, []);

  // Database - RDS
  const handleExportDatabase = useCallback(async () => {
    setLoading(prev => ({ ...prev, exportDb: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      const result = await window.zignagenixAPI.awsExportDatabase();
      if (result.success) {
        setSuccessMessage(`Database exported: ${result.file} (${(result.size / 1024 / 1024).toFixed(2)} MB)`);
      }
    } catch (err) {
      setConnectionError('Failed to export database: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, exportDb: false }));
    }
  }, []);

  const handleCreateRdsInstance = useCallback(async () => {
    if (!rdsConfig.masterUsername || !rdsConfig.masterPassword) {
      setConnectionError('Please enter RDS master username and password');
      return;
    }

    setLoading(prev => ({ ...prev, createRds: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      const result = await window.zignagenixAPI.awsCreateRdsInstance(rdsConfig);
      if (result.success) {
        setRdsInstanceInfo(result);
        setSuccessMessage(`RDS instance created: ${result.instanceId}`);
      }
    } catch (err) {
      setConnectionError('Failed to create RDS: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createRds: false }));
    }
  }, [rdsConfig]);

  const handleCheckRdsStatus = useCallback(async () => {
    setLoading(prev => ({ ...prev, checkRdsStatus: true }));
    setConnectionError(null);

    try {
      const status = await window.zignagenixAPI.awsGetRdsStatus(rdsConfig.instanceId);
      setRdsInstanceInfo(status);
    } catch (err) {
      setConnectionError('Failed to check RDS status: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, checkRdsStatus: false }));
    }
  }, [rdsConfig.instanceId]);

  const handleCreateSecret = useCallback(async () => {
    if (!secretConfig.dbUser || !secretConfig.dbPassword) {
      setConnectionError('Please enter database user and password');
      return;
    }

    setLoading(prev => ({ ...prev, createSecret: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsCreateSecret(secretConfig);
      setSuccessMessage('Secret created in Secrets Manager');
      setSecretConfig({ dbUser: 'zignagenix', dbPassword: '' });
    } catch (err) {
      setConnectionError('Failed to create secret: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, createSecret: false }));
    }
  }, [secretConfig]);

  const handleImportToRds = useCallback(async () => {
    setLoading(prev => ({ ...prev, importToRds: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      // Note: In production, you would select the dump file
      const result = await window.zignagenixAPI.awsImportToRds({ dumpFile: '' });
      setSuccessMessage('Database imported to RDS successfully');
    } catch (err) {
      setConnectionError('Failed to import database: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, importToRds: false }));
    }
  }, []);

  // Monitoring - CloudWatch
  const handleSetupCloudwatch = useCallback(async () => {
    setLoading(prev => ({ ...prev, setupCloudwatch: true }));
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      await window.zignagenixAPI.awsSetupCloudwatch();
      setSuccessMessage('CloudWatch monitoring configured');
    } catch (err) {
      setConnectionError('Failed to setup CloudWatch: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, setupCloudwatch: false }));
    }
  }, []);

  const handleGetCloudwatchStatus = useCallback(async () => {
    setLoading(prev => ({ ...prev, getCloudwatchStatus: true }));
    setConnectionError(null);

    try {
      const status = await window.zignagenixAPI.awsGetCloudwatchStatus();
      setCloudwatchStatus(status);
    } catch (err) {
      setConnectionError('Failed to get CloudWatch status: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, getCloudwatchStatus: false }));
    }
  }, []);

  const handleGetRecentLogs = useCallback(async () => {
    if (!selectedLogGroup) {
      setConnectionError('Please select a log group');
      return;
    }

    setLoading(prev => ({ ...prev, getRecentLogs: true }));
    setConnectionError(null);

    try {
      const logs = await window.zignagenixAPI.awsGetRecentLogs(selectedLogGroup, logTimeRange);
      setRecentLogs(logs);
    } catch (err) {
      setConnectionError('Failed to retrieve logs: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, getRecentLogs: false }));
    }
  }, [selectedLogGroup, logTimeRange]);

  // Backups
  const handleListBackups = useCallback(async () => {
    setLoading(prev => ({ ...prev, listBackups: true }));

    try {
      const backupList = await window.zignagenixAPI.awsListBackups();
      setBackups(backupList);
    } catch (err) {
      console.error('Failed to list backups:', err);
    } finally {
      setLoading(prev => ({ ...prev, listBackups: false }));
    }
  }, []);

  const handleBackupToS3 = useCallback(async () => {
    if (connectionStatus !== 'connected') {
      setConnectionError('Please test AWS connection first');
      return;
    }

    setLoading(prev => ({ ...prev, backup: true }));
    setBackupInProgress(true);
    setUploadProgress(0);
    setConnectionError(null);
    setSuccessMessage(null);

    try {
      const result = await window.zignagenixAPI.awsBackupToS3();
      if (result.success) {
        setUploadProgress(100);
        await new Promise(r => setTimeout(r, 500));
        await handleListBackups();
        setSuccessMessage('Backup completed successfully');
      }
    } catch (err) {
      setConnectionError('Backup failed: ' + err.message);
    } finally {
      setLoading(prev => ({ ...prev, backup: false }));
      setBackupInProgress(false);
      setUploadProgress(0);
    }
  }, [connectionStatus, handleListBackups]);

  const handleDeleteBackup = useCallback(async (key) => {
    if (!window.confirm(`Delete backup: ${key.split('/').pop()}?`)) return;

    try {
      await window.zignagenixAPI.awsDeleteBackup(key);
      await handleListBackups();
      setSuccessMessage('Backup deleted successfully');
    } catch (err) {
      setConnectionError('Delete failed: ' + err.message);
    }
  }, [handleListBackups]);

  const handleDownloadBackup = useCallback(async (key) => {
    try {
      const result = await window.zignagenixAPI.awsDownloadBackup(key);
      if (result.success) {
        setSuccessMessage(`Downloaded to: ${result.filePath}`);
      }
    } catch (err) {
      setConnectionError('Download failed: ' + err.message);
    }
  }, []);

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'configuration':
        return (
          <div className="card aws-section">
            <div className="aws-section-header">
              <Key size={24} />
              <h2>AWS Credentials Configuration</h2>
              <div className={`connection-status-badge status-${connectionStatus}`}>
                {connectionStatus === 'connected' && 'Connected'}
                {connectionStatus === 'testing' && 'Testing...'}
                {connectionStatus === 'error' && 'Error'}
                {connectionStatus === 'disconnected' && 'Disconnected'}
              </div>
            </div>

            <div className="form-section">
              <label className="form-label">AWS Access Key ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="AKIA..."
                value={awsConfig.accessKeyId}
                onChange={(e) => setAwsConfig({ ...awsConfig, accessKeyId: e.target.value })}
              />
            </div>

            <div className="form-section">
              <label className="form-label">Secret Access Key</label>
              <div className="form-input-group">
                <input
                  type={showSecretKey ? 'text' : 'password'}
                  className="form-input"
                  placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                  value={awsConfig.secretAccessKey}
                  onChange={(e) => setAwsConfig({ ...awsConfig, secretAccessKey: e.target.value })}
                />
                <button
                  className="form-input-addon"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  type="button"
                >
                  {showSecretKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-section">
              <label className="form-label">Region</label>
              <select
                className="form-input"
                value={awsConfig.region}
                onChange={(e) => setAwsConfig({ ...awsConfig, region: e.target.value })}
              >
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
                <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                <option value="ca-central-1">Canada (Central)</option>
              </select>
            </div>

            <div className="form-section">
              <label className="form-label">S3 Bucket Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="zignagenix-backups"
                value={awsConfig.bucketName}
                onChange={(e) => setAwsConfig({ ...awsConfig, bucketName: e.target.value })}
              />
            </div>

            <div className="form-section aws-button-group">
              <button
                className="btn btn-primary"
                onClick={handleTestConnection}
                disabled={loading.testConnection}
              >
                {loading.testConnection ? <Loader size={16} className="spin" /> : <Globe size={16} />}
                Test Connection
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveConfig}
                disabled={loading.saveConfig}
              >
                {loading.saveConfig ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
                Save Configuration
              </button>
            </div>

            {accountInfo && (
              <div className="alert alert--success">
                <CheckCircle size={16} />
                <div>
                  <strong>Connected to AWS Account</strong>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    Account ID: {accountInfo.accountId}<br />
                    ARN: {accountInfo.arn}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'infrastructure':
        return (
          <div className="tabs-content">
            {/* Full Status Dashboard */}
            <div className="card aws-section">
              <div className="aws-section-header">
                <Globe size={24} />
                <h2>Full Status Dashboard</h2>
              </div>
              <button className="btn btn-primary" onClick={handleFullStatus} disabled={loading.fullStatus}>
                {loading.fullStatus ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                Refresh Status
              </button>
              {infraFullStatus && (
                <div className="status-cards-grid">
                  <div className="status-card">
                    <Network size={20} />
                    <div className="status-card-label">VPC</div>
                    <div className={`status-card-value ${infraFullStatus.vpc?.status || 'inactive'}`}>
                      {infraFullStatus.vpc?.status || 'Not Created'}
                    </div>
                  </div>
                  <div className="status-card">
                    <Server size={20} />
                    <div className="status-card-label">ECS</div>
                    <div className={`status-card-value ${infraFullStatus.ecs?.status || 'inactive'}`}>
                      {infraFullStatus.ecs?.status || 'Not Created'}
                    </div>
                  </div>
                  <div className="status-card">
                    <Shield size={20} />
                    <div className="status-card-label">ALB</div>
                    <div className={`status-card-value ${infraFullStatus.alb?.status || 'inactive'}`}>
                      {infraFullStatus.alb?.status || 'Not Created'}
                    </div>
                  </div>
                  <div className="status-card">
                    <Database size={20} />
                    <div className="status-card-label">RDS</div>
                    <div className={`status-card-value ${infraFullStatus.rds?.status || 'inactive'}`}>
                      {infraFullStatus.rds?.status || 'Not Created'}
                    </div>
                  </div>
                  <div className="status-card">
                    <Cloud size={20} />
                    <div className="status-card-label">S3</div>
                    <div className={`status-card-value ${infraFullStatus.s3?.status || 'inactive'}`}>
                      {infraFullStatus.s3?.status || 'Not Created'}
                    </div>
                  </div>
                  <div className="status-card">
                    <Activity size={20} />
                    <div className="status-card-label">CloudWatch</div>
                    <div className={`status-card-value ${infraFullStatus.cloudwatch?.status || 'inactive'}`}>
                      {infraFullStatus.cloudwatch?.status || 'Not Configured'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* VPC */}
            <div className="card aws-section">
              <h3>VPC & Networking</h3>
              <div className="form-section aws-button-group">
                <button className="btn btn-primary" onClick={handleCreateVpc} disabled={loading.createVpc}>
                  {loading.createVpc ? <Loader size={16} className="spin" /> : <Network size={16} />}
                  Create VPC
                </button>
                <button className="btn btn-ghost" onClick={handleDescribeVpc} disabled={loading.describeVpc}>
                  {loading.describeVpc ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                  Describe VPC
                </button>
              </div>
              {vpcInfo && (
                <div className="info-block">
                  <strong>VPC ID:</strong> {vpcInfo.vpcId}<br />
                  <strong>CIDR Block:</strong> {vpcInfo.cidrBlock}<br />
                  <strong>State:</strong> {vpcInfo.state}
                </div>
              )}
            </div>

            {/* Security Groups */}
            <div className="card aws-section">
              <h3>Security Groups</h3>
              <div className="form-section">
                <label className="form-label">VPC ID</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="vpc-xxxxxxxx"
                  value={sgVpcId}
                  onChange={(e) => setSgVpcId(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" onClick={handleCreateSg} disabled={loading.createSg}>
                {loading.createSg ? <Loader size={16} className="spin" /> : <Shield size={16} />}
                Create Security Groups
              </button>
            </div>

            {/* S3 Bucket */}
            <div className="card aws-section">
              <h3>S3 Bucket Creation</h3>
              <div className="form-section">
                <label className="form-label">Bucket Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="my-bucket-name"
                  value={s3Config.bucketName}
                  onChange={(e) => setS3Config({ bucketName: e.target.value })}
                />
              </div>
              <button className="btn btn-primary" onClick={handleCreateS3} disabled={loading.createS3}>
                {loading.createS3 ? <Loader size={16} className="spin" /> : <Cloud size={16} />}
                Create S3 Bucket
              </button>
            </div>
          </div>
        );

      case 'compute':
        return (
          <div className="tabs-content">
            {/* IAM Roles */}
            <div className="card aws-section">
              <h3>IAM Roles</h3>
              <p className="form-section-text">Create necessary IAM roles for ECS and application access</p>
              <button className="btn btn-primary" onClick={handleCreateIam} disabled={loading.createIam}>
                {loading.createIam ? <Loader size={16} className="spin" /> : <Key size={16} />}
                Create IAM Roles
              </button>
            </div>

            {/* ECS Cluster */}
            <div className="card aws-section">
              <h3>ECS Cluster</h3>
              <p className="form-section-text">Create an ECS cluster to run containerized services</p>
              <button className="btn btn-primary" onClick={handleCreateEcsCluster} disabled={loading.createEcsCluster}>
                {loading.createEcsCluster ? <Loader size={16} className="spin" /> : <Cpu size={16} />}
                Create ECS Cluster
              </button>
            </div>

            {/* Task Definition */}
            <div className="card aws-section">
              <h3>Task Definition</h3>
              <div className="form-section">
                <label className="form-label">Container Image</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="zignagenix/medical-intelligence:latest"
                  value={taskConfig.containerImage}
                  onChange={(e) => setTaskConfig({ ...taskConfig, containerImage: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">CPU (units)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="256"
                  value={taskConfig.cpu}
                  onChange={(e) => setTaskConfig({ ...taskConfig, cpu: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Memory (MB)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="512"
                  value={taskConfig.memory}
                  onChange={(e) => setTaskConfig({ ...taskConfig, memory: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">RDS Endpoint</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="rds-instance.c123456.us-east-1.rds.amazonaws.com"
                  value={taskConfig.rdsEndpoint}
                  onChange={(e) => setTaskConfig({ ...taskConfig, rdsEndpoint: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Secrets Manager ARN</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="arn:aws:secretsmanager:..."
                  value={taskConfig.secretsArn}
                  onChange={(e) => setTaskConfig({ ...taskConfig, secretsArn: e.target.value })}
                />
              </div>
              <button className="btn btn-primary" onClick={handleRegisterTask} disabled={loading.registerTask}>
                {loading.registerTask ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
                Register Task Definition
              </button>
            </div>

            {/* ECS Service */}
            <div className="card aws-section">
              <h3>ECS Service</h3>
              <div className="form-section">
                <label className="form-label">Desired Count</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="1"
                  value={serviceConfig.desiredCount}
                  onChange={(e) => setServiceConfig({ ...serviceConfig, desiredCount: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Private Subnets (comma-separated)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="subnet-xxxxx,subnet-yyyyy"
                  value={serviceConfig.privateSubnets}
                  onChange={(e) => setServiceConfig({ ...serviceConfig, privateSubnets: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">ECS Security Group ID</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="sg-xxxxxxxx"
                  value={serviceConfig.ecsSgId}
                  onChange={(e) => setServiceConfig({ ...serviceConfig, ecsSgId: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Target Group ARN</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="arn:aws:elasticloadbalancing:..."
                  value={serviceConfig.targetGroupArn}
                  onChange={(e) => setServiceConfig({ ...serviceConfig, targetGroupArn: e.target.value })}
                />
              </div>
              <div className="form-section aws-button-group">
                <button className="btn btn-primary" onClick={handleCreateEcsService} disabled={loading.createEcsService}>
                  {loading.createEcsService ? <Loader size={16} className="spin" /> : <Server size={16} />}
                  Create Service
                </button>
                <button className="btn btn-ghost" onClick={handleDescribeEcs} disabled={loading.describeEcs}>
                  {loading.describeEcs ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                  Describe ECS
                </button>
              </div>
              {ecsStatus && (
                <div className="info-block">
                  <strong>Cluster Name:</strong> {ecsStatus.clusterName}<br />
                  <strong>Services:</strong> {ecsStatus.serviceCount}<br />
                  <strong>Running Tasks:</strong> {ecsStatus.runningTasks}
                </div>
              )}
            </div>
          </div>
        );

      case 'loadbalancer':
        return (
          <div className="tabs-content">
            <div className="card aws-section">
              <div className="aws-section-header">
                <Shield size={24} />
                <h2>Application Load Balancer</h2>
              </div>

              <div className="form-section">
                <label className="form-label">Public Subnets (comma-separated)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="subnet-xxxxx,subnet-yyyyy"
                  value={albConfig.publicSubnets}
                  onChange={(e) => setAlbConfig({ ...albConfig, publicSubnets: e.target.value })}
                />
              </div>

              <div className="form-section">
                <label className="form-label">ALB Security Group ID</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="sg-xxxxxxxx"
                  value={albConfig.albSgId}
                  onChange={(e) => setAlbConfig({ ...albConfig, albSgId: e.target.value })}
                />
              </div>

              <div className="form-section">
                <label className="form-label">VPC ID</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="vpc-xxxxxxxx"
                  value={albConfig.vpcId}
                  onChange={(e) => setAlbConfig({ ...albConfig, vpcId: e.target.value })}
                />
              </div>

              <div className="form-section">
                <label className="form-label">Certificate ARN (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="arn:aws:acm:..."
                  value={albConfig.certificateArn}
                  onChange={(e) => setAlbConfig({ ...albConfig, certificateArn: e.target.value })}
                />
              </div>

              <div className="form-section aws-button-group">
                <button className="btn btn-primary" onClick={handleCreateAlb} disabled={loading.createAlb}>
                  {loading.createAlb ? <Loader size={16} className="spin" /> : <Globe size={16} />}
                  Create ALB
                </button>
                <button className="btn btn-ghost" onClick={handleDescribeAlb} disabled={loading.describeAlb}>
                  {loading.describeAlb ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                  Describe ALB
                </button>
              </div>

              {albStatus && (
                <div className="info-block">
                  <strong>ALB ARN:</strong> {albStatus.arn}<br />
                  <strong>DNS Name:</strong> {albStatus.dnsName}<br />
                  <strong>State:</strong> {albStatus.state}
                </div>
              )}
            </div>
          </div>
        );

      case 'database':
        return (
          <div className="tabs-content">
            {/* Export */}
            <div className="card aws-section">
              <h3>Database Export</h3>
              <p className="form-section-text">Export your local database for migration</p>
              <button className="btn btn-primary" onClick={handleExportDatabase} disabled={loading.exportDb}>
                {loading.exportDb ? <Loader size={16} className="spin" /> : <Download size={16} />}
                Export Database to SQL
              </button>
            </div>

            {/* Create RDS */}
            <div className="card aws-section">
              <h3>Create RDS Instance</h3>
              <div className="form-section">
                <label className="form-label">Instance Class</label>
                <select
                  className="form-input"
                  value={rdsConfig.instanceClass}
                  onChange={(e) => setRdsConfig({ ...rdsConfig, instanceClass: e.target.value })}
                >
                  <option value="db.t3.medium">db.t3.medium (2 vCPU, 1 GB RAM)</option>
                  <option value="db.t3.large">db.t3.large (2 vCPU, 8 GB RAM)</option>
                  <option value="db.r5.large">db.r5.large (2 vCPU, 16 GB RAM)</option>
                  <option value="db.r5.xlarge">db.r5.xlarge (4 vCPU, 32 GB RAM)</option>
                </select>
              </div>
              <div className="form-section">
                <label className="form-label">Storage Size (GB)</label>
                <input
                  type="number"
                  className="form-input"
                  min="20"
                  max="1024"
                  value={rdsConfig.storageGb}
                  onChange={(e) => setRdsConfig({ ...rdsConfig, storageGb: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Master Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={rdsConfig.masterUsername}
                  onChange={(e) => setRdsConfig({ ...rdsConfig, masterUsername: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Master Password</label>
                <div className="form-input-group">
                  <input
                    type={showRdsPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="••••••••"
                    value={rdsConfig.masterPassword}
                    onChange={(e) => setRdsConfig({ ...rdsConfig, masterPassword: e.target.value })}
                  />
                  <button
                    className="form-input-addon"
                    onClick={() => setShowRdsPassword(!showRdsPassword)}
                    type="button"
                  >
                    {showRdsPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="form-section aws-button-group">
                <button className="btn btn-primary" onClick={handleCreateRdsInstance} disabled={loading.createRds}>
                  {loading.createRds ? <Loader size={16} className="spin" /> : <Server size={16} />}
                  Create RDS Instance
                </button>
                <button className="btn btn-ghost" onClick={handleCheckRdsStatus} disabled={loading.checkRdsStatus}>
                  {loading.checkRdsStatus ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                  Check Status
                </button>
              </div>
              {rdsInstanceInfo && (
                <div className="info-block">
                  <strong>Status:</strong> {rdsInstanceInfo.status}<br />
                  {rdsInstanceInfo.endpoint && <><strong>Endpoint:</strong> {rdsInstanceInfo.endpoint}<br /></>}
                  {rdsInstanceInfo.port && <><strong>Port:</strong> {rdsInstanceInfo.port}<br /></>}
                  <strong>Class:</strong> {rdsInstanceInfo.instanceClass}<br />
                  <strong>Storage:</strong> {rdsInstanceInfo.storage} GB<br />
                  <strong>Multi-AZ:</strong> {rdsInstanceInfo.multiAZ ? 'Yes' : 'No'}<br />
                  <strong>Encrypted:</strong> {rdsInstanceInfo.encrypted ? 'Yes' : 'No'}
                </div>
              )}
            </div>

            {/* Secrets Manager */}
            <div className="card aws-section">
              <h3>Secrets Manager</h3>
              <p className="form-section-text">Store database credentials securely</p>
              <div className="form-section">
                <label className="form-label">Database User</label>
                <input
                  type="text"
                  className="form-input"
                  value={secretConfig.dbUser}
                  onChange={(e) => setSecretConfig({ ...secretConfig, dbUser: e.target.value })}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Database Password</label>
                <div className="form-input-group">
                  <input
                    type={showSecretPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="••••••••"
                    value={secretConfig.dbPassword}
                    onChange={(e) => setSecretConfig({ ...secretConfig, dbPassword: e.target.value })}
                  />
                  <button
                    className="form-input-addon"
                    onClick={() => setShowSecretPassword(!showSecretPassword)}
                    type="button"
                  >
                    {showSecretPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleCreateSecret} disabled={loading.createSecret}>
                {loading.createSecret ? <Loader size={16} className="spin" /> : <Lock size={16} />}
                Create Secret
              </button>
            </div>

            {/* Import */}
            <div className="card aws-section">
              <h3>Import Database to RDS</h3>
              <p className="form-section-text">Import the exported database dump into RDS</p>
              <button className="btn btn-primary" onClick={handleImportToRds} disabled={loading.importToRds}>
                {loading.importToRds ? <Loader size={16} className="spin" /> : <Upload size={16} />}
                Import to RDS
              </button>
            </div>
          </div>
        );

      case 'monitoring':
        return (
          <div className="tabs-content">
            {/* CloudWatch Setup */}
            <div className="card aws-section">
              <h3>CloudWatch Monitoring</h3>
              <div className="form-section aws-button-group">
                <button className="btn btn-primary" onClick={handleSetupCloudwatch} disabled={loading.setupCloudwatch}>
                  {loading.setupCloudwatch ? <Loader size={16} className="spin" /> : <Activity size={16} />}
                  Setup CloudWatch
                </button>
                <button className="btn btn-ghost" onClick={handleGetCloudwatchStatus} disabled={loading.getCloudwatchStatus}>
                  {loading.getCloudwatchStatus ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                  Get Status
                </button>
              </div>
              {cloudwatchStatus && (
                <div className="info-block">
                  <strong>Log Groups:</strong> {cloudwatchStatus.logGroupCount}<br />
                  <strong>Alarms:</strong> {cloudwatchStatus.alarmCount}<br />
                  <strong>Status:</strong> {cloudwatchStatus.status}
                </div>
              )}
            </div>

            {/* Recent Logs */}
            <div className="card aws-section">
              <h3>Recent Logs Viewer</h3>
              <div className="form-section">
                <label className="form-label">Log Group Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="/aws/ecs/zignagenix-service"
                  value={selectedLogGroup}
                  onChange={(e) => setSelectedLogGroup(e.target.value)}
                />
              </div>
              <div className="form-section">
                <label className="form-label">Time Range (minutes)</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="60"
                  value={logTimeRange}
                  onChange={(e) => setLogTimeRange(parseInt(e.target.value))}
                />
              </div>
              <button className="btn btn-primary" onClick={handleGetRecentLogs} disabled={loading.getRecentLogs}>
                {loading.getRecentLogs ? <Loader size={16} className="spin" /> : <Download size={16} />}
                Get Logs
              </button>
              {recentLogs.length > 0 && (
                <div className="logs-viewer">
                  {recentLogs.map((log, idx) => (
                    <div key={idx} className="log-entry">
                      <span className="log-timestamp">{log.timestamp}</span>
                      <span className="log-message">{log.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'backups':
        return (
          <div className="tabs-content">
            <div className="card aws-section">
              <div className="aws-section-header">
                <Archive size={24} />
                <h2>Application Backup to S3</h2>
              </div>

              <div className="form-section aws-button-group">
                <button className="btn btn-primary" onClick={handleBackupToS3} disabled={loading.backup || backupInProgress}>
                  {loading.backup ? <Loader size={16} className="spin" /> : <Upload size={16} />}
                  {backupInProgress ? 'Backing up...' : 'Backup Now'}
                </button>
                <button className="btn btn-ghost" onClick={handleListBackups} disabled={loading.listBackups}>
                  {loading.listBackups ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                  Refresh List
                </button>
              </div>

              {backupInProgress && (
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                </div>
              )}

              {backups.length > 0 ? (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Filename</th>
                        <th>Size</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backups.map((backup) => (
                        <tr key={backup.key}>
                          <td><code style={{ fontSize: '11px' }}>{backup.filename}</code></td>
                          <td>{(backup.size / 1024 / 1024).toFixed(2)} MB</td>
                          <td>{new Date(backup.lastModified).toLocaleString()}</td>
                          <td className="action-cell">
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleDownloadBackup(backup.key)}
                              title="Download"
                            >
                              <Download size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteBackup(backup.key)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <HardDrive size={32} />
                  <p>No backups found</p>
                  <small>Click "Backup Now" to create one</small>
                </div>
              )}
            </div>
          </div>
        );

      case 'hipaa':
        return (
          <div className="tabs-content">
            <div className="card aws-section">
              <div className="aws-section-header">
                <Shield size={24} />
                <h2>HIPAA Compliance Checklist</h2>
              </div>
              <p className="form-section-text">Verify all HIPAA compliance requirements are met</p>
              <button className="btn btn-primary" onClick={handleFullStatus} disabled={loading.fullStatus} style={{ marginBottom: '20px' }}>
                {loading.fullStatus ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                Auto-Check from AWS
              </button>

              <div className="checklist">
                {hipaaChecklist.map(item => (
                  <div key={item.id} className={`checklist-item ${item.checked ? 'checked' : ''}`}>
                    {item.checked ? (
                      <CheckCircle size={20} style={{ color: '#62BB46' }} />
                    ) : (
                      <AlertTriangle size={20} style={{ color: '#F26522' }} />
                    )}
                    <span className="checklist-label">{item.label}</span>
                    <span className={`checklist-status ${item.checked ? 'status-checked' : 'status-pending'}`}>
                      {item.checked ? '✓ Verified' : '○ Pending'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="compliance-summary">
                <strong>Compliance Status:</strong> {hipaaChecklist.filter(i => i.checked).length} / {hipaaChecklist.length} requirements met
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="page aws-cloud-page">
      <div className="page-header-row">
        <div>
          <div className="page-title">
            <Cloud size={32} />
            AWS Cloud Deployment
          </div>
          <div className="page-subtitle">Comprehensive dashboard for cloud infrastructure, deployments, and HIPAA compliance</div>
        </div>
        <PrintButton pageTitle="AWS Cloud Deployment" subtitle="Cloud Infrastructure & HIPAA Compliance" />
      </div>

      {connectionError && (
        <div className="alert alert--error">
          <AlertTriangle size={16} />
          {connectionError}
        </div>
      )}

      {successMessage && (
        <div className="alert alert--success">
          <CheckCircle size={16} />
          {successMessage}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tabs-container">
        <DraggableTabBar
          tabs={[
            { id: 'configuration', label: 'Configuration', icon: Key },
            { id: 'infrastructure', label: 'Infrastructure', icon: Globe },
            { id: 'compute', label: 'Compute (ECS)', icon: Cpu },
            { id: 'loadbalancer', label: 'Load Balancer', icon: Shield },
            { id: 'database', label: 'Database', icon: Database },
            { id: 'monitoring', label: 'Monitoring', icon: Activity },
            { id: 'backups', label: 'Backups', icon: Archive },
            { id: 'hipaa', label: 'HIPAA', icon: Shield },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          prefKey="aws_tab_order"
          className="tabs-nav"
          tabClassName="tab-button"
          activeClassName="active"
          iconSize={18}
        />
      </div>

      {/* Tab Content */}
      <div className="tab-content-wrapper">
        {renderTabContent()}
      </div>
    </div>
  );
}

export default AWSCloudPage;
